import { cloneDeep } from 'lodash';
import { IGcfComponentConfig } from 'src/interfaces/Component';
import {
  IGcfProductListGroup,
  IGcfProductListProduct,
  IGcfProductListProductImage,
  IGcfProductListProductSize,
  IGcfProductListProductSizeVariation,
} from 'src/interfaces/Product';

import {
  findProductAndGroupByUid,
  findProductByUid,
} from '../../MenuBoardModificationPage.helper';
import type {
  IAttributeFlavor,
  IAttributeSize,
} from './ProductOverrideRightSideBar.helper';
import { DEFAULT_SORT_ORDER } from './ProductOverrideRightSideBar.helper';
import { IAttributeChanges } from './ProductOverrideRightSideBar.interface';

const syncSizesAndVariations = (
  targetProduct: IGcfProductListProduct,
  attributeSizes: IAttributeSize[],
): void => {
  if (!targetProduct.sizes) {
    targetProduct.sizes = {};
  }

  const sizesMap = targetProduct.sizes as Record<
    string,
    IGcfProductListProductSize
  >;

  const currentSizeKeys = new Set(Object.keys(sizesMap).map((k) => k));

  const sizeOrderMap = new Map<string, number>();
  const sizeMap = new Map<string, IAttributeSize>();
  attributeSizes.forEach((s) => {
    const nameKey = s.name;
    sizeOrderMap.set(nameKey, s.order);
    sizeMap.set(nameKey, s);
  });

  const sizeKeysToRemove = new Set<string>();
  for (const size of attributeSizes) {
    const sizeKey = size.name;
    const hasCheckedFlavor = size.flavors.some((f) => f.checked);
    if ((size.checked || hasCheckedFlavor) && !currentSizeKeys.has(sizeKey)) {
      sizesMap[size.name] = {
        order: sizeOrderMap.get(sizeKey) ?? DEFAULT_SORT_ORDER,
        name: size.name,
        config: {
          showLabel: true,
          showPrice: true,
          showCalories: true,
          showFlavors: true,
        },
        variations: {},
      };
    } else if (
      !size.checked &&
      !hasCheckedFlavor &&
      currentSizeKeys.has(sizeKey)
    ) {
      sizeKeysToRemove.add(sizeKey);
    }
  }

  if (sizeKeysToRemove.size > 0) {
    const remainingEntries = Object.entries(sizesMap).filter(
      ([key]) => !sizeKeysToRemove.has(key),
    );
    targetProduct.sizes = Object.fromEntries(remainingEntries);
  }

  const updatedSizesMap = targetProduct.sizes as Record<
    string,
    IGcfProductListProductSize
  >;

  for (const [key, sizeEntry] of Object.entries(updatedSizesMap)) {
    const matchingSize = sizeMap.get(key);
    if (matchingSize) {
      sizeEntry.order = sizeOrderMap.get(key) ?? DEFAULT_SORT_ORDER;

      const variationsMap = sizeEntry.variations as Record<
        string,
        IGcfProductListProductSizeVariation
      >;
      const currentVariationKeys = new Set(
        Object.keys(variationsMap).map((v) => v),
      );

      const variationKeysToRemove = new Set<string>();
      for (const flavor of matchingSize.flavors as IAttributeFlavor[]) {
        const flavorKey = flavor.productCode;
        if (flavor.checked && !currentVariationKeys.has(flavorKey)) {
          variationsMap[flavorKey] = {
            order: matchingSize.flavors.indexOf(flavor),
            productCode: flavor.productCode,
            config: {},
          };
        } else if (!flavor.checked && currentVariationKeys.has(flavorKey)) {
          variationKeysToRemove.add(flavorKey);
        } else if (flavor.checked && currentVariationKeys.has(flavorKey)) {
          variationsMap[flavorKey].order = matchingSize.flavors.indexOf(flavor);
        }
      }

      if (variationKeysToRemove.size > 0) {
        sizeEntry.variations = Object.fromEntries(
          Object.entries(variationsMap).filter(
            ([vKey]) => !variationKeysToRemove.has(vKey),
          ),
        );
      }
    }
  }
};

const getSelectedFlavorImage = (
  images?: IGcfProductListProductImage[],
): IGcfProductListProductImage | undefined =>
  images?.find((img) => img.visible && img.assetName) ??
  images?.find((img) => img.assetName);

const applyImageVisibilityToConfig = (
  targetProduct: IGcfProductListProduct,
  showImage: boolean,
  defaultSize?: string,
  updatedFlavorAssetMap?: Record<string, IGcfProductListProductImage[]>,
  selectedFlavorProductCode?: string,
  selectedFlavorImages?: IGcfProductListProductImage[],
): void => {
  const sizesMap = (targetProduct.sizes ?? {}) as Record<
    string,
    IGcfProductListProductSize
  >;
  const defaultSizeKey = defaultSize ?? Object.keys(sizesMap)[0];

  for (const sizeEntry of Object.values(
    sizesMap,
  ) as IGcfProductListProductSize[]) {
    const sizeMatches =
      defaultSizeKey !== undefined && (sizeEntry.name ?? '') === defaultSizeKey;

    const variationList = sizeEntry.variations
      ? (
          Object.values(
            sizeEntry.variations,
          ) as IGcfProductListProductSizeVariation[]
        )
          .slice()
          .sort(
            (a, b) =>
              (a.order ?? DEFAULT_SORT_ORDER) - (b.order ?? DEFAULT_SORT_ORDER),
          )
      : [];

    for (const variation of variationList) {
      const visible =
        showImage === true &&
        sizeMatches &&
        variation.productCode === selectedFlavorProductCode;

      if (!variation.config) {
        variation.config = {};
      }
      const images = variation.config.images;
      if (!images || images.length === 0) {
        if (visible) {
          const selectedImage = getSelectedFlavorImage(selectedFlavorImages);

          variation.config.images = selectedImage
            ? [{ ...selectedImage, visible: true }]
            : [{ visible: true }];
        }
      } else {
        const updatedFlavorAssets =
          updatedFlavorAssetMap?.[selectedFlavorProductCode ?? ''];

        if (
          variation.productCode === selectedFlavorProductCode &&
          updatedFlavorAssets
        ) {
          // replace selected product image
          variation.config.images = updatedFlavorAssets.map((item) => {
            if (!item.visible) {
              return item;
            }

            const existingImage = images.find((img) => img.visible);

            return {
              ...item,
              style: existingImage?.style ?? item.style,
              outageStamp: existingImage?.outageStamp ?? item.outageStamp,
            };
          });
        } else if (visible && !images.some((img) => img.assetName)) {
          const selectedImage = getSelectedFlavorImage(selectedFlavorImages);
          const existingImage = images.find((img) => img.visible) ?? images[0];

          variation.config.images = selectedImage
            ? [
                {
                  ...selectedImage,
                  style: existingImage?.style ?? selectedImage.style,
                  outageStamp:
                    existingImage?.outageStamp ?? selectedImage.outageStamp,
                  visible: true,
                },
              ]
            : images.map((img) => ({ ...img, visible }));
        } else {
          variation.config.images = images.map((img) => ({
            ...img,
            visible,
          }));
        }
      }
    }
  }
};

const persistDefaultSize = (
  targetProduct: IGcfProductListProduct,
  defaultSize?: string,
): void => {
  if (!targetProduct.config) {
    targetProduct.config = {} as IGcfProductListProduct['config'];
  }
  targetProduct.config.defaultSize = defaultSize;
};

export const extractVariationsFromConfig = (
  config: IGcfComponentConfig,
): { code: number | string }[] => {
  const groups = config?.productList?.groups ?? {};

  const codes = (Object.values(groups) as IGcfProductListGroup[]).flatMap(
    (group) =>
      Object.values(group.products || {}).flatMap((product) => {
        // Group product code.
        const productCodes = product.productCode ? [product.productCode] : [];

        // Selected variation codes (size.variations only holds checked flavors).
        const variationCodes = Object.values(product.sizes || {}).flatMap(
          (size) =>
            Object.values(size.variations || {})
              .map((variation) => variation.productCode)
              .filter((code): code is string => Boolean(code)),
        );

        return [...productCodes, ...variationCodes];
      }),
  );

  // Dedupe so the same productCode appearing in multiple products/groups isn't
  // posted as a duplicate in schedule_item_override_product_variations.
  return [...new Set(codes)].map((code) => ({ code }));
};

export interface IComputeUpdatedConfigResult {
  componentConfig: IGcfComponentConfig;
  scheduleItemOverrideProductVariations: {
    code: number | string;
  }[];
}

export const transformProductKeyToOrderKey = (
  selectProductKey: string,
  componentConfig: IGcfComponentConfig,
) => {
  const productAndGroup = findProductAndGroupByUid(
    componentConfig,
    selectProductKey,
  );

  if (
    !productAndGroup ||
    !productAndGroup.product?.order ||
    !productAndGroup.groupKey
  ) {
    return '';
  }

  return `${productAndGroup.groupKey}-${productAndGroup.product.order}`;
};

export const computeUpdatedComponentConfig = (
  config: IGcfComponentConfig,
  selectProductKey: string,
  changes: IAttributeChanges,
): IComputeUpdatedConfigResult => {
  const newConfig = cloneDeep(config);
  const targetProduct = findProductByUid(newConfig, selectProductKey);

  if (targetProduct && changes.attributeSizes) {
    syncSizesAndVariations(targetProduct, changes.attributeSizes);
  }

  if (targetProduct && changes.showImage !== undefined) {
    const selectedFlavor = changes.attributeSizes
      ?.find((s) => s.name === changes.defaultSize)
      ?.flavors.find((f) => f.images?.some((img) => img.visible));

    applyImageVisibilityToConfig(
      targetProduct,
      changes.showImage,
      changes.defaultSize,
      changes.updatedFlavorAssetMap,
      selectedFlavor?.productCode,
      selectedFlavor?.images,
    );
  }

  if (targetProduct && 'defaultSize' in changes) {
    persistDefaultSize(targetProduct, changes.defaultSize);
  }

  const scheduleItemOverrideProductVariations =
    extractVariationsFromConfig(newConfig);

  return {
    componentConfig: newConfig,
    scheduleItemOverrideProductVariations,
  };
};
